import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Film, Eye, Clock, Plus, Trash2, Check, SortAsc, SortDesc, Heart, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getUserMovies, updateMovie, deleteMovie, getImageUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MovieCard = ({ movie, onUpdate, onDelete, token }) => {
  const poster = getImageUrl(movie.poster_path, 'w342');
  const isWatched = movie.status === 'watched';

  const handleToggleStatus = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const newStatus = isWatched ? 'watchlist' : 'watched';
      await axios.put(
        `${API_URL}/api/movies/${movie.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onUpdate(movie.id, { status: newStatus });
      toast.success(newStatus === 'watched' ? 'Gemarkeerd als gekeken' : 'Terug naar watchlist');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Kon status niet bijwerken');
    }
  };

  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await onDelete(movie.id);
      toast.success('Film verwijderd');
    } catch (error) {
      toast.error('Kon film niet verwijderen');
    }
  };

  return (
    <Link 
      to={`/movie/${movie.tmdb_id}`}
      className="group relative aspect-[2/3] rounded-lg overflow-hidden bg-gray-900"
      data-testid={`movie-card-${movie.tmdb_id}`}
    >
      {poster ? (
        <img src={poster} alt={movie.title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-800">
          <Film className="w-12 h-12 text-gray-600" />
        </div>
      )}

      {/* Status Badge */}
      {isWatched ? (
        <div className="absolute top-2 left-2 bg-green-500 rounded-full p-1.5">
          <Check className="w-3 h-3 text-white" />
        </div>
      ) : (
        <div className="absolute top-2 left-2 bg-pink-500 rounded-full p-1.5">
          <Heart className="w-3 h-3 text-white fill-current" />
        </div>
      )}

      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <h3 className="text-white font-semibold text-sm mb-2 line-clamp-2">{movie.title}</h3>
          
          <div className="flex gap-2" onClick={e => e.preventDefault()}>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleToggleStatus}
              className="flex-1 text-xs bg-white/10 hover:bg-white/20"
            >
              {isWatched ? <Eye className="w-3 h-3 mr-1" /> : <Check className="w-3 h-3 mr-1" />}
              {isWatched ? 'Watchlist' : 'Gekeken'}
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={handleDelete}
              className="px-2"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
};

// Local storage keys for persisting user preferences
const MOVIES_SORT_KEY = 'episo_movies_sort';
const MOVIES_SORT_DIR_KEY = 'episo_movies_sort_dir';

export const MoviesPage = () => {
  const { isAuthenticated, token } = useAuth();
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [sortBy, setSortBy] = useState(() => localStorage.getItem(MOVIES_SORT_KEY) || 'added_at');
  const [sortDir, setSortDir] = useState(() => localStorage.getItem(MOVIES_SORT_DIR_KEY) || 'desc');

  // Persist sort preferences
  useEffect(() => {
    localStorage.setItem(MOVIES_SORT_KEY, sortBy);
  }, [sortBy]);

  useEffect(() => {
    localStorage.setItem(MOVIES_SORT_DIR_KEY, sortDir);
  }, [sortDir]);

  const fetchMovies = async () => {
    try {
      const data = await getUserMovies();
      setMovies(data.movies || []);
    } catch (error) {
      console.error('Error fetching movies:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchMovies();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const handleUpdate = (movieId, updates) => {
    setMovies(prev => prev.map(m => 
      m.id === movieId ? { ...m, ...updates } : m
    ));
  };

  const handleDelete = async (movieId) => {
    await deleteMovie(movieId);
    setMovies(prev => prev.filter(m => m.id !== movieId));
  };

  const filteredMovies = movies.filter(m => {
    if (activeTab === 'all') return true;
    if (activeTab === 'watchlist') return m.status === 'watchlist';
    if (activeTab === 'watched') return m.status === 'watched';
    return true;
  });

  // Sort movies with direction support
  const sortedMovies = [...filteredMovies].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case 'added_at':
        comparison = new Date(b.added_at) - new Date(a.added_at);
        break;
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'watched_at':
        if (!a.watched_at) return 1;
        if (!b.watched_at) return -1;
        comparison = new Date(b.watched_at) - new Date(a.watched_at);
        break;
      case 'runtime':
        comparison = (b.runtime || 0) - (a.runtime || 0);
        break;
      default:
        comparison = 0;
    }
    return sortDir === 'asc' ? -comparison : comparison;
  });

  const watchlistCount = movies.filter(m => m.status === 'watchlist').length;
  const watchedCount = movies.filter(m => m.status === 'watched').length;

  if (!isAuthenticated) {
    return (
      <div data-testid="movies-page" className="min-h-screen p-6 md:p-12">
        <div className="max-w-2xl mx-auto text-center py-20">
          <Film className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h1 className="text-2xl font-display font-bold mb-4">Mijn Films</h1>
          <p className="text-gray-400 mb-8">Log in om je filmbibliotheek te bekijken en beheren.</p>
          <Link to="/login">
            <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full px-8 py-6">
              Inloggen
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="movies-page" className="min-h-screen p-6 md:p-12">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-display font-bold mb-2">Mijn Films</h1>
        <p className="text-gray-400">
          {watchedCount} gekeken • {watchlistCount} op watchlist
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-2">
            <Eye className="w-5 h-5 text-green-400" />
            <span className="text-gray-400 text-sm">Gekeken</span>
          </div>
          <p className="text-2xl font-display font-bold">{watchedCount}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-2">
            <Heart className="w-5 h-5 text-pink-400" />
            <span className="text-gray-400 text-sm">Watchlist</span>
          </div>
          <p className="text-2xl font-display font-bold">{watchlistCount}</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-2">
            <Clock className="w-5 h-5 text-purple-400" />
            <span className="text-gray-400 text-sm">Kijktijd</span>
          </div>
          <p className="text-2xl font-display font-bold">
            {Math.round(movies.filter(m => m.status === 'watched').reduce((sum, m) => sum + (m.runtime || 0), 0) / 60)}u
          </p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-2">
            <Film className="w-5 h-5 text-yellow-400" />
            <span className="text-gray-400 text-sm">Totaal</span>
          </div>
          <p className="text-2xl font-display font-bold">{movies.length}</p>
        </div>
      </div>

      {/* Filters Row */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList className="bg-white/5 border border-white/10">
            <TabsTrigger 
              value="all" 
              data-testid="tab-all"
              className="data-[state=active]:bg-indigo-600"
            >
              Alles ({movies.length})
            </TabsTrigger>
            <TabsTrigger 
              value="watchlist" 
              data-testid="tab-watchlist"
              className="data-[state=active]:bg-indigo-600"
            >
              Watchlist ({watchlistCount})
            </TabsTrigger>
            <TabsTrigger 
              value="watched" 
              data-testid="tab-watched"
              className="data-[state=active]:bg-indigo-600"
            >
              Gekeken ({watchedCount})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-48 bg-white/5 border-white/10">
              <SelectValue placeholder="Sorteren op" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="added_at">Laatst toegevoegd</SelectItem>
              <SelectItem value="title">Titel (A-Z)</SelectItem>
              <SelectItem value="watched_at">Laatst gekeken</SelectItem>
              <SelectItem value="runtime">Duur</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')}
            className="bg-white/5 border border-white/10 hover:bg-white/10"
            title={sortDir === 'asc' ? 'Oplopend' : 'Aflopend'}
          >
            {sortDir === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Movies Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : sortedMovies.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {sortedMovies.map((movie) => (
            <MovieCard 
              key={movie.id} 
              movie={movie} 
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              token={token}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Film className="w-16 h-16 text-gray-700 mx-auto mb-4" />
          <p className="text-gray-400 mb-4">
            {activeTab === 'all' 
              ? 'Je hebt nog geen films toegevoegd'
              : activeTab === 'watchlist'
              ? 'Je watchlist is leeg'
              : 'Je hebt nog geen films gekeken'}
          </p>
          <Link to="/search">
            <Button className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
              <Plus className="w-4 h-4 mr-2" />
              Films zoeken
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

export default MoviesPage;
